import { Component, OnInit, Input, Optional, Inject } from '@angular/core';
import { TaxLot, TaxLotDetail } from '../../../models/common/trading-common.model';
import { TradingDataService } from '../../../services/common/trading-data-service.service';
import { TAXLOT_DISPOSITION_METHOD_LIST } from '../../../utils/trading-constants';
import { GridOptions, ClientSideRowModelModule, LicenseManager, Module } from '@ag-grid-enterprise/all-modules';
import { PershingGridHeaderComponent } from '@pershing/pershing-grid2';
import { LogService } from '@pershing/pershing-core';
import { DecimalPipe } from '@angular/common';
import { get } from 'lodash';
import { TradingEntitlements } from './../../../models/common/trading-entitlements';
import { TradingService } from '../../../services/common/trading-service.service';
@Component({
  selector: 'tax-lot-review',
  templateUrl: './tax-lot-review.component.html',
  styleUrls: ['./tax-lot-review.component.css'],
  providers: [DecimalPipe]
})
export class TaxLotReviewComponent implements OnInit {
  @Input() taxlot: TaxLot;
  @Input() ordQty: number;
  @Input() shareQty: number;
  @Input() dollarQty: number;
  @Input() tradeType: string;
  @Input() isChangeOrder: boolean;
  @Input() tradingEntitlements: TradingEntitlements;
  activeTheme: string;
  public modules: Module[] = [ClientSideRowModelModule];
  dispMethodCode: string;
  dispMethodStr: string;
  availQty: number;
  showTaxlotsGrid: boolean = false;
  isTaxlotsConfirmed: boolean = false;
  showAltMessage: boolean = false;
  // isDollarQty and isShareQty is used for Mutual Fund Taxlots
  isDollarQty: boolean = false;
  isShareQty: boolean = false;

  useComfortableTpl = false;
  useCompactTpl = false;
  useMultilegTpl = false;

  // isOrdQtyis is used for Equity and Option Taxlots
  isOrdQty: boolean = false;
  altMessage: string;
  totalSelQty: number;
  taxLotDetails: TaxLotDetail[];
  totalLots: number = 0;
  rowData: any[];
  gridOptions: GridOptions;
  labelClassStr = 'col-xs-6 col-sm-6 medium-weight';
  valueClassStr = 'col-xs-6 col-sm-6';
  columnDefs: any;

  private formatEqtMfQtyCellValue(param: any): string {
    let allowFractionalSharesTrading = this.tradingEntitlements.isSellFractionalSharesWithEntitlementEnabled === 'N' ? true : this.tradingEntitlements.isEntitledToFractionalDollar;
    if ((this.tradeType === 'EQUITY' && allowFractionalSharesTrading) || (this.tradeType === 'MUTUAL_FUND')){
      return this.decimalPipe.transform(param.value, '1.4-4');
    } else {
      return this.decimalPipe.transform(param.value, '1.0-0');
    }
  }

  constructor(private tradingDataService: TradingDataService, private tradingService: TradingService, private logService: LogService, private decimalPipe: DecimalPipe,
    @Optional() @Inject('AppSettings') appSettings: any ) {
    // App Settings is optional, so we need to do a NULL-safe operation here.
    if ( get(appSettings, 'agGridEnterpriseKey')) {
      LicenseManager.setLicenseKey(appSettings.agGridEnterpriseKey);
  }

   }

  ngOnInit() {
    this.activeTheme = this.tradingService.getActiveThemeFromCurrentContainer();
    this.generateGridColumns();
    this.showTaxlotsGrid = false;
    this.isTaxlotsConfirmed = false;
    this.showAltMessage = false;
    if (this.tradeType === 'EQUITY' || this.tradeType === 'OPTION') {
      this.isDollarQty = false;
      this.isShareQty = false;
      this.isOrdQty = true;
    } else if (this.tradeType === 'MUTUAL_FUND') {
      if (this.dollarQty !== undefined && this.dollarQty !== null) {
        this.isDollarQty = true;
        this.isShareQty = false;
        this.isOrdQty = false;
      } else if (this.shareQty !== undefined && this.shareQty !== null) {
        this.isDollarQty = false;
        this.isShareQty = true;
        this.isOrdQty = false;
      }
    }

    if ( this.tradeType==='OPTION' && this.tradingEntitlements.isEntitledToComplexOption ) {
        this.useMultilegTpl = true;
    } else if ( this.activeTheme === 'compact' ) {
        this.useCompactTpl = true;
    } else if ( this.activeTheme === 'comfortable' ) {
        this.useComfortableTpl = true;
    }

    if (this.isChangeOrder) {
      this.labelClassStr = 'col-xs-4 col-sm-4 medium-weight';
      this.valueClassStr = 'col-xs-4 col-sm-4 bold-weight';
    }

    if (!!this.taxlot) {
      this.handleTaxLot();
    }
  }

  private generateGridColumns() {
    this.columnDefs = [
      { headerName: 'TAX_LOT.TRADE_DATE', field: 'strPurchaseDate', minWidth: 100, width: 100, headerClass: 'center', cellClass: 'center' },
      { headerName: 'TAX_LOT.AVAILABLE_QTY', field: 'availableQty', minWidth: 107, width: 107, headerClass: 'center', cellClass: 'ag-right-aligned-cell',
        valueFormatter: (param) => {
          return this.decimalPipe.transform(param.value, '1.4-4');
          },
      },
      { headerName: 'TAX_LOT.SELECTED_QTY', field: 'disposeQty', minWidth: 105, width: 105, headerClass: 'center', cellClass: 'ag-right-aligned-cell',
        valueFormatter: (param) => {
          return this.formatEqtMfQtyCellValue(param);
        }
      },
      { headerName: 'TAX_LOT.UNIT_COST', field: 'unitCostAmt', minWidth: 95, width: 95, headerClass: 'center', cellClass: 'ag-right-aligned-cell',
        valueFormatter: (param) => {
          return this.decimalPipe.transform(param.value, '1.2-4');
        }
      }
    ];
  }

  private handleTaxLot():void {
    if (this.taxlot.errorCode !== 0) {
      this.showTaxlotsGrid = false;
      this.logService.error('taxLotService.getTaxlotDetails response error - ', this.taxlot);
      return;
    }
    this.isTaxlotsConfirmed = true;
    this.availQty = this.tradingDataService.availableQty;
    if (!!this.taxlot.sellMethodCode) {
      this.dispMethodCode = this.taxlot.sellMethodCode.trim().toUpperCase();
    } else if (!!this.tradingDataService.defaultDispositionMethodResp && !!this.tradingDataService.defaultDispositionMethodResp.defaultSellMethodCode) {
      this.dispMethodCode = this.tradingDataService.defaultDispositionMethodResp.defaultSellMethodCode.trim().toUpperCase();
      this.fetchTaxlots();
    }
    if (!!this.dispMethodCode) {
      const method = TAXLOT_DISPOSITION_METHOD_LIST.find(i => i.code === this.dispMethodCode);
      if ( !!method ) {
        this.dispMethodStr = method.label || '';
      } else {
        this.dispMethodStr = '';
      }
    }
  }

  fetchTaxlots() {
    this.showTaxlotsGrid = true;
    if (this.taxlot.returnCode === 0 && !!this.taxlot.taxLotDetails) {
      this.totalLots = this.taxlot.taxLotDetails.length;
      this.altMessage = '';
      this.setGridOptions();
      let rowList = this.getInitRowData(this.taxlot.taxLotDetails);
      this._refreshGrid(rowList);
    } else {
      this.logService.log('taxLotService.getTaxlotDetails taxLotDetails error - ', this.taxlot);
      this.showAltMessage = true;
      this.altMessage = this.taxlot.returnMsg;
    }

  }

  getInitRowData(taxLotDetails) {
    let rowList = [];
    this.totalSelQty = 0;
    if (taxLotDetails) {
      taxLotDetails.forEach((taxlot: TaxLotDetail) => {
        const totalDisposeQty = Number(taxlot.disposeQty);
        this.totalSelQty += totalDisposeQty;
        let row: any = taxlot;
        rowList.push(row);
      });
    }
    return rowList;
  }

  _refreshGrid(newRowList) {
    this.rowData = newRowList;
  }

  setGridOptions() {
    this.gridOptions = {
      rowHeight: this.activeTheme == 'compact' ? 26 : 44,
      headerHeight: this.activeTheme == 'compact' ? 26 : 44,
      suppressMovableColumns: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      domLayout: 'autoHeight', // You need to disable this to get fullWidthRow working
      onGridSizeChanged: () => {
        if (this.gridOptions.api) {
          this.gridOptions.api.sizeColumnsToFit();
          this.gridOptions.api.setDomLayout("print");
        }
      }
    };
    this.gridOptions.defaultColDef = {
      headerComponentFramework: PershingGridHeaderComponent,
      resizable: true
    };
  }
}
